import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Bell, X, ArrowRight, Sparkles, Loader2 } from "lucide-react";

/**
 * In-app inactivity banner. Polls /api/notifications on mount and every 10 min.
 * Triggers when last activity is >= 5 days old. LLM-generated personalized copy.
 */
export default function InactivityBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notif, setNotif] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user || user.role !== "student" || user.onboarded === false) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/notifications");
      const top = (data?.notifications || [])[0] || null;
      setNotif(top);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const id = window.setInterval(fetchNotifications, 10 * 60 * 1000); // 10 min
    return () => window.clearInterval(id);
  }, [fetchNotifications]);

  const onCta = () => {
    if (!notif) return;
    if (notif.project_id) {
      navigate(`/dashboard/projects/${notif.project_id}`);
    } else {
      navigate("/dashboard");
    }
  };

  const onDismiss = async () => {
    if (!notif || dismissing) return;
    setDismissing(true);
    const id = notif.notification_id;
    // optimistic
    setNotif(null);
    try {
      await api.post(`/notifications/${id}/dismiss`);
    } catch {
      toast.error("No se pudo descartar el aviso");
    } finally {
      setDismissing(false);
    }
  };

  if (loading || !notif) return null;

  const days = notif.metadata?.days_inactive ?? null;

  return (
    <div
      data-testid="inactivity-banner"
      data-notification-id={notif.notification_id}
      className="mx-4 lg:mx-8 mt-4 rounded-2xl border border-copper/40 bg-[#FBF6EC] overflow-hidden animate-fade-up"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-5 sm:px-6 py-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="grid place-items-center h-10 w-10 shrink-0 rounded-full bg-forest text-paper">
            <Sparkles size={15} className="text-[#E0C58A]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
                Tu tutor de IA
              </p>
              {typeof days === "number" && days > 0 && (
                <span
                  data-testid="inactivity-banner-days"
                  className="inline-flex items-center gap-1 text-[10.5px] text-ink-soft bg-paper-2 rounded-full px-2 py-0.5"
                >
                  <Bell size={10} /> {days} días sin escribir
                </span>
              )}
            </div>
            <p
              data-testid="inactivity-banner-title"
              className="mt-1 font-serif text-lg sm:text-xl text-ink leading-snug text-balance"
            >
              {notif.title}
            </p>
            <p
              data-testid="inactivity-banner-body"
              className="mt-1.5 text-[13px] text-ink leading-relaxed"
            >
              {notif.body}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:flex-col sm:items-end shrink-0">
          <button
            data-testid="inactivity-banner-cta"
            onClick={onCta}
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all shadow-sm whitespace-nowrap"
          >
            {notif.cta_label || "Regresar al editor"}
            <ArrowRight
              size={13}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
          <button
            data-testid="inactivity-banner-dismiss"
            onClick={onDismiss}
            disabled={dismissing}
            aria-label="Descartar aviso"
            className="h-7 w-7 grid place-items-center rounded-full text-ink-soft hover:bg-white hover:text-ink transition-colors disabled:opacity-60"
          >
            {dismissing ? <Loader2 size={12} className="animate-spin" /> : <X size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}
